import { SelectProps, Select as _Select } from 'antd';

import { CollapseDown } from '../../icons';
import { getClassName } from '../_utils';

import './Select.scss';

type Props<U extends string | number, V extends U | readonly U[]> = Pick<
  SelectProps<V>,
  | 'allowClear'
  | 'aria-label'
  | 'aria-labelledby'
  | 'className'
  | 'defaultValue'
  | 'disabled'
  | 'id'
  | 'mode'
  | 'popupClassName'
  | 'style'
  | 'tokenSeparators'
> & {
  value?: V | undefined;
  options?: SelectOptions<U>;
  onChange?: (value: V) => void;
};

export type SelectOption<U extends string | number> = {
  label: React.ReactNode;
  value: U;
};

export type SelectOptions<U extends string | number> =
  readonly SelectOption<U>[];

/**
 * @deprecated use `Select.One`, `Select.Multiple`, `Select.Tags`
 */
export function Select<U extends string | number, V extends U | readonly U[]>({
  value,
  options,
  className,
  style,
  ...props
}: Props<U, V>) {
  return (
    <_Select
      value={value}
      options={options as SelectOption<U>[]}
      optionFilterProp={options ? 'label' : 'value'}
      suffixIcon={<CollapseDown />}
      {...(props.mode === 'multiple' || props.mode === 'tags'
        ? {
            maxTagCount: 'responsive',
            maxTagPlaceholder: (omittedValues) => (
              <span className="max-length">{`+${omittedValues.length}`}</span>
            ),
          }
        : {})}
      {...props}
      className={getClassName('select', className)}
      style={{ width: '100%', ...style }}
    />
  );
}

Select.One = function Select_One<T extends string | number>(
  props: Omit<Props<T, T>, 'mode' | 'tokenSeparators'>,
) {
  return <Select<T, T> {...props} />;
};

Select.Multiple = function Select_Multiple<T extends string | number>(
  props: Omit<Props<T, readonly T[]>, 'mode' | 'tokenSeparators'>,
) {
  return <Select<T, readonly T[]> mode="multiple" {...props} />;
};

// TODO: restrict T to string
Select.Tags = function Select_Tags<T extends string | number>(
  props: Omit<Props<T, readonly T[]>, 'mode'>,
) {
  return (
    <Select<T, readonly T[]> mode="tags" tokenSeparators={[',']} {...props} />
  );
};
